/*============================================================================
  Cart rewards — free shipping progress bar + automatic free gift

  Reads its configuration from window.cartRewardsSettings
  (snippets/cart-rewards-settings.liquid) and:

    - renders the progress bar into every [data-cart-rewards] container
      (cart page + ajax cart drawer),
    - adds the gift line item as soon as the cart qualifies,
    - removes it again as soon as it doesn't.

  The gift is tagged with the `_free_gift` line item property. Properties starting
  with an underscore are hidden from the customer in checkout and order emails, and
  the gift's own value is always excluded from the qualifying subtotal so it can
  never keep itself in the cart.

  Making the gift cost nothing is a merchandising decision, not a theme one: either
  the chosen variant is priced 0, or an automatic discount in Shopify admin zeroes
  it. The line item renders "was / Free" whenever its final price lands on 0.
==============================================================================*/
(function () {
  'use strict';

  var settings = window.cartRewardsSettings;
  if (!settings) {
    return;
  }

  // Note the script keeps running when the feature is switched off: the bar renders
  // nothing, but gift lines already sitting in customers' carts still get cleaned up.

  var GIFT_PROPERTY = settings.giftProperty || '_free_gift';
  var RELOAD_GUARD_KEY = 'cart-rewards:reloads';
  var MAX_GUARDED_RELOADS = 2;
  var MAX_ADD_ATTEMPTS = 3;

  // Set while we are writing to the cart, so the cart events our own writes
  // trigger don't start a second round of writes.
  var isSyncing = false;
  // Set when the gift can't be added (sold out between page load and checkout,
  // cart limits, an app rejecting the line). Stops us retrying in a loop — it only
  // ever blocks adding, never removing, and clears once the cart stops qualifying.
  var isGiftBlocked = false;
  // Counts adds that didn't leave a gift line behind — if something downstream
  // keeps stripping the line, we stop asking rather than hammer the cart.
  var addAttempts = 0;

  var canWriteToCart = typeof window.fetch === 'function' && typeof window.Promise === 'function';

  // Used only if a merchant clears a message field in the theme editor.
  var MESSAGE_FALLBACKS = {
    locked: 'Spend {{ amount }} more to unlock {{ reward }}.',
    partial: 'Spend {{ amount }} more to unlock {{ reward }}.',
    unlocked: 'Congratulations! You have unlocked every reward.'
  };

  var ICONS = {
    truck:
      '<svg viewBox="0 0 34 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
        '<path d="M0.8 8.4h4.6"/><path d="M0.8 12.6h3"/>' +
        '<path d="M8 4.6h13.2v11.2H8z"/>' +
        '<path d="M21.2 8.8h4.9l4 4.1v2.9h-8.9z"/>' +
        '<circle cx="13" cy="18.2" r="2.3"/><circle cx="25.2" cy="18.2" r="2.3"/>' +
      '</svg>',
    gift:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
        '<path d="M20.5 11.5v9.2a.8.8 0 0 1-.8.8H4.3a.8.8 0 0 1-.8-.8v-9.2"/>' +
        '<path d="M2.6 7.4h18.8v4.1H2.6z"/>' +
        '<path d="M12 7.4v14.1"/>' +
        '<path d="M12 7.4S9.2 7.4 7.9 6.2a2.2 2.2 0 0 1 2.7-3.4c1.2.7 1.4 4.6 1.4 4.6z"/>' +
        '<path d="M12 7.4s2.8 0 4.1-1.2a2.2 2.2 0 0 0-2.7-3.4C12.2 3.5 12 7.4 12 7.4z"/>' +
      '</svg>'
  };

  /*============================================================================
    Helpers
  ==============================================================================*/

  function jq() {
    if (window.wetheme && window.wetheme.$) {
      return window.wetheme.$;
    }
    return window.jQuery || null;
  }

  // timber.js defines Shopify.formatMoney and loads first; the fallback only covers
  // the case where that file is missing or moved.
  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, settings.moneyFormat);
    }

    var format = settings.moneyFormat || '{{amount}}';
    var placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var noDecimals = placeholder && placeholder[1].indexOf('no_decimals') !== -1;
    var commaSeparator = placeholder && placeholder[1].indexOf('comma_separator') !== -1;

    var amount = (Math.round(cents) / 100).toFixed(noDecimals ? 0 : 2);
    var parts = amount.split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + (commaSeparator ? '.' : ','));
    amount = parts.join(commaSeparator ? ',' : '.');

    return placeholder ? format.replace(placeholder[0], amount) : amount;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function isGiftItem(item) {
    return !!(item && item.properties && item.properties[GIFT_PROPERTY]);
  }

  // Everything the customer chose themselves, after line level discounts.
  // The gift never counts towards the thresholds that put it in the cart.
  function qualifyingSubtotal(cart) {
    if (!cart || !cart.items) {
      return 0;
    }

    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (!isGiftItem(cart.items[i])) {
        total += cart.items[i].final_line_price;
      }
    }
    return total;
  }

  function milestones() {
    var list = [];

    if (settings.shipping && settings.shipping.enabled && settings.shipping.threshold > 0) {
      list.push({
        key: 'shipping',
        threshold: settings.shipping.threshold,
        label: settings.shipping.label,
        icon: ICONS.truck
      });
    }

    if (settings.gift && settings.gift.enabled && settings.gift.threshold > 0) {
      list.push({
        key: 'gift',
        threshold: settings.gift.threshold,
        label: settings.gift.label,
        icon: ICONS.gift
      });
    }

    return list.sort(function (a, b) {
      return a.threshold - b.threshold;
    });
  }

  /*============================================================================
    Cart requests
  ==============================================================================*/

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          throw new Error(data.description || data.message || 'Cart request failed');
        }
        return data;
      });
    });
  }

  function fetchCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      return response.json();
    });
  }

  function changeQuantity(key, quantity) {
    return postJSON('/cart/change.js', { id: key, quantity: quantity });
  }

  // /cart/change.js answers with the updated cart, so we can confirm the line really
  // went. If a key based change is ignored — some apps rewrite line items, which
  // invalidates keys mid-flight — fall back to the line's index in that same response.
  function removeGiftLine(line) {
    return changeQuantity(line.key, 0).then(function (cart) {
      var items = (cart && cart.items) || [];
      var index = -1;

      for (var i = 0; i < items.length; i++) {
        if (items[i].key === line.key) {
          index = i + 1; // the API indexes lines from 1
          break;
        }
      }

      if (index === -1) {
        return cart;
      }

      return postJSON('/cart/change.js', { line: index, quantity: 0 });
    });
  }

  function addGift() {
    var properties = {};
    properties[GIFT_PROPERTY] = 'true';

    return postJSON('/cart/add.js', {
      items: [
        {
          id: settings.gift.variantId,
          quantity: settings.gift.quantity || 1,
          properties: properties
        }
      ]
    });
  }

  /*============================================================================
    Rendering
  ==============================================================================*/

  function messageFor(list, subtotal, nextMilestone) {
    var template;

    var messages = settings.messages || {};

    if (!nextMilestone) {
      template = messages.unlocked || MESSAGE_FALLBACKS.unlocked;
    } else if (nextMilestone === list[0]) {
      template = messages.locked || MESSAGE_FALLBACKS.locked;
    } else {
      template = messages.partial || MESSAGE_FALLBACKS.partial;
    }

    var remaining = nextMilestone ? Math.max(0, nextMilestone.threshold - subtotal) : 0;
    var rewardName = nextMilestone ? nextMilestone.label : list[list.length - 1].label;

    return String(template || '')
      .replace(/\{\{\s*amount\s*\}\}/g, '<span class="cart-rewards__amount">' + formatMoney(remaining) + '</span>')
      .replace(/\{\{\s*reward\s*\}\}/g, escapeHtml(rewardName));
  }

  function barHtml(list, subtotal) {
    var maxThreshold = list[list.length - 1].threshold;
    var percent = Math.max(0, Math.min(100, (subtotal / maxThreshold) * 100));

    var nextMilestone = null;
    for (var i = 0; i < list.length; i++) {
      if (subtotal < list[i].threshold) {
        nextMilestone = list[i];
        break;
      }
    }

    var milestonesHtml = '';
    for (var j = 0; j < list.length; j++) {
      var milestone = list[j];
      var unlocked = subtotal >= milestone.threshold;
      var position = Math.max(0, Math.min(100, (milestone.threshold / maxThreshold) * 100));

      milestonesHtml +=
        '<div class="cart-rewards__milestone' + (unlocked ? ' is-unlocked' : '') + '" style="left:' + position + '%">' +
          '<span class="cart-rewards__milestone-icon">' + milestone.icon + '</span>' +
          '<span class="cart-rewards__milestone-label">' + escapeHtml(milestone.label) + '</span>' +
        '</div>';
    }

    return (
      '<div class="cart-rewards__inner">' +
        '<div class="cart-rewards__head">' +
          '<span class="cart-rewards__head-icon">' + list[0].icon + '</span>' +
          '<p class="cart-rewards__message" aria-live="polite">' + messageFor(list, subtotal, nextMilestone) + '</p>' +
        '</div>' +
        '<div class="cart-rewards__progress">' +
          '<div class="cart-rewards__track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' + Math.round(percent) + '">' +
            '<span class="cart-rewards__fill" style="width:' + percent + '%"></span>' +
          '</div>' +
          milestonesHtml +
        '</div>' +
      '</div>'
    );
  }

  function render(subtotal) {
    var containers = document.querySelectorAll('[data-cart-rewards]');
    if (!containers.length) {
      return;
    }

    var list = milestones();
    var html = list.length ? barHtml(list, subtotal) : '';

    for (var i = 0; i < containers.length; i++) {
      var container = containers[i];
      var context = container.getAttribute('data-cart-rewards-context');

      if (!list.length || (context === 'drawer' && !settings.showInDrawer) || (context === 'page' && !settings.showOnCartPage)) {
        container.classList.remove('is-active');
        container.innerHTML = '';
        continue;
      }

      container.innerHTML = html;
      container.setAttribute('data-subtotal', subtotal);
      container.classList.add('is-active');
    }
  }

  function setBusy(busy) {
    var containers = document.querySelectorAll('[data-cart-rewards]');
    for (var i = 0; i < containers.length; i++) {
      containers[i].classList.toggle('is-busy', !!busy);
    }
  }

  /*============================================================================
    Reload guard (cart page only)

    The cart page is server rendered, so a gift added or removed there needs a
    reload to show up. The counter makes sure a cart that refuses to settle
    (a gift an app keeps stripping out, for instance) can't reload forever.
  ==============================================================================*/

  function readReloadCount() {
    try {
      return parseInt(window.sessionStorage.getItem(RELOAD_GUARD_KEY) || '0', 10) || 0;
    } catch (error) {
      return 0;
    }
  }

  function writeReloadCount(count) {
    try {
      window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(count));
    } catch (error) {
      /* Private mode — the guard is a nicety, not a requirement. */
    }
  }

  function clearReloadGuard() {
    try {
      window.sessionStorage.removeItem(RELOAD_GUARD_KEY);
    } catch (error) {
      /* no-op */
    }
  }

  function reloadCartPage() {
    var count = readReloadCount();
    if (count >= MAX_GUARDED_RELOADS) {
      console.warn('[cart-rewards] Stopped reloading the cart page: the free gift state would not settle.');
      return;
    }

    writeReloadCount(count + 1);
    window.location.reload();
  }

  /*============================================================================
    Gift sync
  ==============================================================================*/

  function giftActionsFor(cart) {
    var actions = [];
    var giftLines = [];
    var i;

    for (i = 0; i < cart.items.length; i++) {
      if (isGiftItem(cart.items[i])) {
        giftLines.push(cart.items[i]);
      }
    }

    if (giftLines.length) {
      addAttempts = 0;
    }

    // Removal is deliberately unconditional. A gift this script put in the cart has
    // to come back out whenever it isn't earned — including when the gift has since
    // been switched off, gone out of stock, or been reconfigured, all of which leave
    // gift.enabled false. Gating this on the same flags as adding is what stranded
    // gifts in the cart.
    var giftConfigured = !!(
      settings.enabled &&
      settings.gift.enabled &&
      settings.gift.variantId &&
      settings.gift.threshold > 0
    );
    var eligible = giftConfigured && qualifyingSubtotal(cart) >= settings.gift.threshold;

    if (!eligible) {
      // A cart that stops qualifying also earns a clean slate for adding later.
      isGiftBlocked = false;
      addAttempts = 0;

      giftLines.forEach(function (line) {
        actions.push(function () {
          return removeGiftLine(line);
        });
      });
      return actions;
    }

    if (isGiftBlocked) {
      return actions;
    }

    var wantedVariantId = settings.gift.variantId;
    var wantedQuantity = settings.gift.quantity || 1;
    var keptLine = null;

    giftLines.forEach(function (line) {
      // Keep one line for the currently configured gift; drop stale gifts
      // (the merchant changed the product) and any duplicates.
      if (!keptLine && line.variant_id === wantedVariantId) {
        keptLine = line;
        return;
      }

      actions.push(function () {
        return changeQuantity(line.key, 0);
      });
    });

    if (!keptLine) {
      if (addAttempts >= MAX_ADD_ATTEMPTS) {
        isGiftBlocked = true;
        console.warn('[cart-rewards] Gave up adding the free gift: it does not stay in the cart.');
        return [];
      }

      addAttempts++;
      actions.push(addGift);
    } else if (keptLine.quantity !== wantedQuantity) {
      actions.push(function () {
        return changeQuantity(keptLine.key, wantedQuantity);
      });
    }

    return actions;
  }

  function refreshAfterSync(context, cart) {
    // On the cart page the line items are server rendered, so any write has to be
    // followed by a reload — even when the change came out of the drawer.
    if (context === 'page' || settings.isCartPage) {
      reloadCartPage();
      return;
    }

    if (window.ajaxCart && typeof window.ajaxCart.load === 'function') {
      // Rebuilds the drawer, which fires afterCartLoad.ajaxCart and re-renders the bar.
      window.ajaxCart.load();
      return;
    }

    fetchCart()
      .then(function (freshCart) {
        render(qualifyingSubtotal(freshCart));
      })
      .catch(function () {
        render(qualifyingSubtotal(cart));
      });
  }

  function sync(cart, context) {
    if (!cart || !cart.items) {
      return;
    }

    render(qualifyingSubtotal(cart));

    if (isSyncing || !canWriteToCart) {
      return;
    }

    var actions = giftActionsFor(cart);
    if (!actions.length) {
      // Cart and settings agree — nothing to do, and nothing to guard against.
      clearReloadGuard();
      return;
    }

    isSyncing = true;
    setBusy(true);

    actions
      .reduce(function (chain, action) {
        return chain.then(action);
      }, Promise.resolve())
      .then(function () {
        isSyncing = false;
        setBusy(false);
        refreshAfterSync(context, cart);
      })
      .catch(function (error) {
        isSyncing = false;
        isGiftBlocked = true;
        setBusy(false);
        console.warn('[cart-rewards] Could not update the free gift:', (error && error.message) || error);
      });
  }

  /*============================================================================
    Boot
  ==============================================================================*/

  function paintFromServerValue() {
    var container = document.querySelector('[data-cart-rewards][data-subtotal]');
    if (!container) {
      return;
    }
    render(parseInt(container.getAttribute('data-subtotal'), 10) || 0);
  }

  function init() {
    paintFromServerValue();

    // Cart drawer: every ajax cart rebuild re-renders the bar and re-checks the gift.
    var $ = jq();
    if ($) {
      $(document.body).on('afterCartLoad.ajaxCart', function (event, cart) {
        sync(cart, 'drawer');
      });
    }

    // Cart page: reconcile the server-rendered cart against the live one.
    if (settings.isCartPage && canWriteToCart) {
      fetchCart()
        .then(function (cart) {
          sync(cart, 'page');
        })
        .catch(function (error) {
          console.warn('[cart-rewards] Could not read the cart:', (error && error.message) || error);
        });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.CartRewards = {
    render: render,
    sync: sync,
    isGiftItem: isGiftItem,
    qualifyingSubtotal: qualifyingSubtotal
  };
})();
